import moment from "moment";
import { scoreTree, periodGrain, keyLabel, rowsOf } from "../scoreFindings";
import { CONFIG } from "../CONFIG";

/**
 * The insight context: the map scored over the last few periods (so every
 * sub-topic has a short trajectory of positions), plus the same period
 * scored per dataset (for channel contrasts) and per topic (for
 * concentration and escalation). Pure — the scoring module called a handful
 * of times over the client-side tree.
 */

const GRAIN_UNIT = { month: "month", week: "isoWeek", year: "year" };
const GRAIN_STEP = { month: "month", week: "week", year: "year" };
const GRAIN_FORMAT = { month: "YYYY-MM", week: "GGGG-[W]WW", year: "YYYY" };

// PM defaults for tenants whose tree carries no category list; the tree's
// `signalCategories` (with polarity) wins whenever present.
const PM_DEFAULT_POLARITY = { userDissatisfaction: "negative", userSatisfaction: "positive" };

// A synthetic period option for one window of the grain — the shape
// scoreTree reads (a grain-bearing `value`, from/to for the window).
const periodAt = (start, grain) => {
  const from = start.clone().startOf(GRAIN_UNIT[grain]);
  const key = from.format(GRAIN_FORMAT[grain]);
  const value =
    grain === "month" ? `month_${from.format("YYYY_MM")}` : grain === "year" ? `year_${from.format("YYYY")}` : `${key}_week`;
  return {
    key,
    label: keyLabel(key, grain),
    value,
    from: from.toISOString(),
    to: from.clone().add(1, GRAIN_STEP[grain]).toISOString(),
  };
};

// The last `count` periods of the grain, oldest first, ending on the
// selected one.
export const insightPeriods = (period, grain, count) => {
  const start = moment(period.from).startOf(GRAIN_UNIT[grain]);
  return Array.from({ length: count }, (_, i) => periodAt(start.clone().subtract(count - 1 - i, GRAIN_STEP[grain]), grain));
};

// The tree with only the observations `keep` accepts; rows and buckets left
// with nothing drop out.
export const filterTreeObservations = (tree, keep) => {
  const buckets = (tree?.tree || [])
    .map((bucket) => {
      const parked = (bucket.parkedObservations || []).filter(keep);
      if (!bucket.children?.length) {
        const observations = (bucket.observations || []).filter(keep);
        return observations.length || parked.length ? { ...bucket, observations, parkedObservations: parked } : null;
      }
      const children = bucket.children
        .map((row) => {
          const observations = (row.observations || []).filter(keep);
          return observations.length ? { ...row, observations } : null;
        })
        .filter(Boolean);
      return children.length || parked.length ? { ...bucket, children, parkedObservations: parked } : null;
    })
    .filter(Boolean);
  return { ...tree, tree: buckets, unplacedObservations: (tree?.unplacedObservations || []).filter(keep) };
};

const bucketObservations = (bucket) => [
  ...rowsOf(bucket).flatMap((r) => r.observations || []),
  ...(bucket.parkedObservations || []),
];

// Per period, the topic's leading signal category and how much of the
// topic each category held — the series "escalation" reads.
const leadingCategoryByPeriod = (bucket, periods, grain, signalMetaById) => {
  const obs = bucketObservations(bucket);
  return periods.map((p) => {
    const counts = {};
    let total = 0;
    for (const o of obs) {
      if (!o.dateThatMatters || moment(o.dateThatMatters).format(GRAIN_FORMAT[grain]) !== p.key) continue;
      const category = o.signalId ? signalMetaById[String(o.signalId)]?.category : null;
      if (!category) continue;
      counts[category] = (counts[category] || 0) + 1;
      total += 1;
    }
    const leading = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || null;
    return { key: p.key, label: p.label, counts, total, leading };
  });
};

/**
 * @returns null when the period has no grain (all time) — nothing to detect
 *   against — else the context every detector reads.
 */
export const buildInsightContext = ({ historyTree, period, signalMetaById = {}, signalCategories = null, fallbackDatedDatasets }) => {
  const grain = periodGrain(period);
  if (!grain || !period?.from || !historyTree) return null;

  const periods = insightPeriods(period, grain, CONFIG.INSIGHTS.PERIODS);
  const now = periods[periods.length - 1];
  const scoreAt = (tree, p, g = "findings") =>
    scoreTree({ historyTree: tree, period: p, signalMetaById, signalCategories, fallbackDatedDatasets, grain: g });
  const itemsOf = (scored) => [...scored.priorities, ...scored.wins];

  // Trajectories: every sub-topic scored in every period. scoreTree keeps
  // every row that has observations anywhere in its history, so the series
  // is complete — a period the sub-topic was absent from shows as n = 0.
  const trajectories = new Map();
  let current = null;
  for (const p of periods) {
    const scored = scoreAt(historyTree, p);
    if (p === now) current = scored;
    for (const item of itemsOf(scored)) {
      if (!trajectories.has(item.nodeId)) trajectories.set(item.nodeId, { item: null, points: [] });
      trajectories.get(item.nodeId).points.push({
        key: p.key,
        label: p.label,
        zone: item.zone,
        severity: item.severity.value,
        available: !!item.momentum.available,
        index: item.momentum.available ? item.momentum.index : null,
        band: item.momentum.available ? item.momentum.band : null,
        confidence: item.momentum.confidence ?? 0,
        n: item.momentum.nNow ?? 0,
      });
    }
  }
  for (const item of itemsOf(current)) trajectories.get(item.nodeId).item = item;

  // The current period scored inside each dataset alone.
  const datasets = [...new Set(itemsOf(current).flatMap((i) => i.datasets))];
  const byDataset = new Map(
    datasets.map((ds) => [
      ds,
      new Map(itemsOf(scoreAt(filterTreeObservations(historyTree, (o) => o.dataset === ds), now)).map((i) => [i.nodeId, i])),
    ])
  );

  // Topics: the bucket scored as a whole, its rows as current items, and
  // its category mix per period.
  const bucketItems = new Map(itemsOf(scoreAt(historyTree, now, "buckets")).map((i) => [i.nodeId, i]));
  const buckets = (historyTree.tree || []).map((bucket) => ({
    nodeId: String(bucket.nodeId),
    label: bucket.label,
    item: bucketItems.get(String(bucket.nodeId)) || null,
    rows: rowsOf(bucket)
      .map((r) => trajectories.get(String(r.nodeId))?.item)
      .filter(Boolean),
    leadingByPeriod: leadingCategoryByPeriod(bucket, periods, grain, signalMetaById),
  }));
  const bucketOfNode = new Map();
  for (const b of buckets) for (const r of b.rows) bucketOfNode.set(r.nodeId, b);

  const polarityOf = (category) =>
    signalCategories?.find((c) => c.value === category)?.polarity || PM_DEFAULT_POLARITY[category] || "neutral";

  return { grain, periods, now, current, trajectories, byDataset, buckets, bucketOfNode, polarityOf };
};
